import { useCallback, useRef, useState } from 'react';
import { BRAZILIAN_STATES } from '@/constants/states';
import { fetchAddressByCep, isValidCep } from '@/services/geocode.service';
import { FormInstance } from 'antd';
import { getCitiesByState } from '@/services/city.service';

type Props = {
  form: FormInstance;
};

export function useAddressForm({ form }: Props) {
  const [cepError, setCepError] = useState<string | null>(null);
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAllowManualFill, setIsAllowManualFill] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [lastCepFetched, setLastCepFetched] = useState<string>('');
  const [isCepValidated, setIsCepValidated] = useState(false);
  const [cityApiFailed, setCityApiFailed] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const stateOptions = BRAZILIAN_STATES.map((state) => ({
    label: `${state.sigla} - ${state.nome}`,
    value: state.sigla,
    disabled: false,
  }));

  const cityOptions = cities.map((city) => ({
    label: city,
    value: city,
    disabled: false,
  }));

  const fetchAddressByCepDebounced = useCallback(
    async (cleanCep: string) => {
      setLastCepFetched(cleanCep);

      try {
        setLoading(true);
        const addressData = await fetchAddressByCep(cleanCep);

        const isValidAddress = addressData &&
          addressData.street &&
          addressData.city &&
          addressData.state;

        if (isValidAddress) {
          form.setFieldsValue({
            street: addressData.street,
            district: addressData.district,
            city: addressData.city,
            state: addressData.state.toUpperCase(),
          });

          await loadCitiesForState(addressData.state.toUpperCase());

          setCepError(null);
          setIsCepValidated(true);
          setIsAllowManualFill(false);
        } else {
          form.setFieldsValue({
            street: undefined,
            district: undefined,
            city: undefined,
            state: undefined,
          });
          setCities([]);
          setCepError('CEP não encontrado. Preencha o endereço manualmente.');
          setIsCepValidated(true);
          setIsAllowManualFill(true);
        }
      } catch (error) {
        setCepError('Não foi possível buscar o endereço. Preencha manualmente.');
        setIsCepValidated(true);
        setIsAllowManualFill(true);
      } finally {
        setLoading(false);
      }
    },
    [form]
  );

  const handleCepChange = (e: any) => {
    const value = e?.target?.value || e;

    if (!value || typeof value !== 'string') {
      setCepError(null);
      setLastCepFetched('');
      setIsCepValidated(false);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      return;
    }

    const cleanCep = value.replace(/\D/g, '');

    setCepError(null);
    setIsCepValidated(false);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (cleanCep.length === 0 || cleanCep.length < 8) {
      setLastCepFetched('');
    }

    if (cleanCep.length > 0 && cleanCep.length < 8) {
      setCepError('CEP deve ter 8 dígitos');
      return;
    }

    if (cleanCep.length === 8 && isValidCep(cleanCep)) {
      if (cleanCep !== lastCepFetched) {
        debounceTimer.current = setTimeout(() => {
          fetchAddressByCepDebounced(cleanCep);
        }, 800);
      }
    }
  };

  const loadCitiesForState = async (stateCode: string) => {
    if (!stateCode) {
      setCities([]);
      setCityApiFailed(false);
      return;
    }

    setLoadingCities(true);
    try {
      const citiesList = await getCitiesByState(stateCode);
      if (citiesList.length > 0) {
        setCities(citiesList);
        setCityApiFailed(false);
      } else {
        setCities([]);
        setCityApiFailed(true);
      }
    } catch {
      setCities([]);
      setCityApiFailed(true);
    } finally {
      setLoadingCities(false);
    }
  };

  const handleStateChange = async (value: string) => {
    form.setFieldValue('city', undefined);
    await loadCitiesForState(value);
  };

  const resetAddressForm = () => {
    setCepError(null);
    setCities([]);
    setLastCepFetched('');
    setIsCepValidated(false);
    setIsAllowManualFill(false);
    setCityApiFailed(false);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  };

  const markCepAsValidated = () => {
    setIsCepValidated(true);
  };

  return {
    cities,
    loading,
    cepError,
    cityOptions,
    stateOptions,
    loadingCities,
    isCepValidated,
    cityApiFailed,
    handleCepChange,
    handleStateChange,
    resetAddressForm,
    markCepAsValidated,
    isAllowManualFill,
  };
}
